import { RRule } from 'rrule';
import moment from 'moment';

/**
 * Expands a recurring event into individual instances based on its recurrence rule
 * @param event The original event with a recurrence_rule
 * @param startDate The start date for expansion (default: 3 months ago)
 * @param endDate The end date for expansion (default: 6 months from now)
 * @returns Array of event instances
 */
export function expandRecurringEvent(event, startDate, endDate) {
  if (!event.recurrence_rule) {
    return [];
  }

  try {
    // Parse the recurrence rule
    const rruleString = event.recurrence_rule.replace('RRULE:', '');
    const options = RRule.parseString(rruleString);
    
    // Set the start date (dtstart) based on the event's date
    const eventDate = moment(event.shift_date || event.date);
    
    if (event.start_time && !event.is_all_day) {
      const [hour, minute, second] = event.start_time.split(':').map(Number);
      eventDate.hour(hour).minute(minute).second(second || 0);
    } else {
      eventDate.hour(0).minute(0).second(0);
    }
    
    options.dtstart = eventDate.toDate();
    
    // Set the expansion range
    const rangeStart = startDate || moment().subtract(3, 'months').toDate();
    const rangeEnd = endDate || moment().add(6, 'months').toDate();
    
    // Create the RRule object
    const rule = new RRule(options);
    
    // Generate instances within the range
    const instances = rule.between(rangeStart, rangeEnd, true);
    
    // Convert instances to event objects
    return instances.map(instanceDate => {
      const shiftDate = moment(instanceDate).format('YYYY-MM-DD');
      
      // Calculate start and end times for this instance
      let start, end;
      if (event.is_all_day) {
        start = moment(instanceDate).startOf('day').toDate();
        end = moment(instanceDate).endOf('day').toDate();
      } else {
        // For non-all-day events, use the original event's time but on the instance date
        const originalStartTime = moment(event.start);
        const originalEndTime = moment(event.end);
        
        // Create new start/end times with the instance date but original times
        start = moment(shiftDate)
          .hour(originalStartTime.hour())
          .minute(originalStartTime.minute())
          .second(originalStartTime.second())
          .toDate();
          
        end = moment(shiftDate)
          .hour(originalEndTime.hour())
          .minute(originalEndTime.minute())
          .second(originalEndTime.second())
          .toDate();
      }
      
      return {
        ...event,
        id: `${event.id}_${shiftDate}`,
        start,
        end,
        shift_date: shiftDate,
        isRecurringInstance: true,
        originalEventId: event.id,
        instanceDate: shiftDate,
      };
    });
  } catch (error) {
    return [];
  }
}

/**
 * Expands all recurring events in a list
 * @param events Array of events
 * @param startDate Start date for expansion
 * @param endDate End date for expansion
 * @returns Array of events with recurring instances expanded
 */
export function expandAllRecurringEvents(events, startDate, endDate) {
  const expandedEvents = [];
  
  events.forEach(event => {
    if (event.recurrence_rule) {
      // Add the recurring instances
      const instances = expandRecurringEvent(event, startDate, endDate);
      expandedEvents.push(...instances);
      
      // Don't add the original recurring event to avoid duplication
      // Only add instances
    } else {
      // Add non-recurring events as-is
      expandedEvents.push(event);
    }
  });
  
  return expandedEvents;
}

/**
 * Checks if an event is a recurring instance
 */
export function isRecurringInstance(event) {
  return event && event.isRecurringInstance === true;
}

/**
 * Gets the original event ID for a recurring instance
 */
export function getOriginalEventId(event) {
  if (isRecurringInstance(event)) {
    return event.originalEventId;
  }
  return event.id;
} 